// Servizio per le impostazioni utente - usa API reali
import api from '../api/axios'

export interface UserProfile {
  id?: number
  firstName: string
  lastName: string
  email: string
  username?: string
  role: string
  language: string
  workingHours: string
  profilePhoto: string | null
}

// Interfaccia per i dati utente dal backend
export interface BackendUser {
  id: number
  username: string
  email: string
  first_name: string
  last_name: string
  role: string
  is_superuser: boolean
  language?: string
  working_hours?: string
  profile_photo_url?: string | null
}

export interface GeneralPreferences {
  // Tema gestito da ThemeContext (localStorage key: hexa-theme).
  // Riservata per future preferenze locali.
}

export interface UserSettings {
  profile: UserProfile
  preferences: GeneralPreferences
}

const DEFAULT_PREFERENCES: GeneralPreferences = {}

// Chiave per salvare le preferenze locali in localStorage
const SETTINGS_STORAGE_KEY = 'hexa_user_settings'

// Carica le preferenze locali da localStorage
const loadLocalSettings = (): { preferences: GeneralPreferences } => {
  try {
    const stored = localStorage.getItem(SETTINGS_STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      return {
        preferences: parsed.preferences || DEFAULT_PREFERENCES,
      }
    }
  } catch (e) {
    console.warn('[Settings] Error loading local settings:', e)
  }
  return {
    preferences: DEFAULT_PREFERENCES,
  }
}

// Salva le preferenze locali in localStorage
const saveLocalSettings = (preferences: GeneralPreferences) => {
  try {
    localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify({ preferences }))
  } catch (e) {
    console.warn('[Settings] Error saving local settings:', e)
  }
}

// Converte i dati backend in profilo frontend
const mapBackendToProfile = (userData: BackendUser): UserProfile => ({
  id: userData.id,
  firstName: userData.first_name || '',
  lastName: userData.last_name || '',
  email: userData.email || '',
  username: userData.username || '',
  role: userData.role || 'user',
  language: userData.language || 'it',
  workingHours: userData.working_hours || 'lun-ven-9-18',
  profilePhoto: userData.profile_photo_url || null,
})

// API Service per le impostazioni
export const settingsService = {
  // Ottieni tutte le impostazioni dell'utente
  getUserSettings: async (): Promise<UserSettings> => {
    // Carica il profilo dal backend
    const response = await api.get('api/accounts/users/me/')
    const userData = response.data as BackendUser

    // Carica le preferenze locali
    const localSettings = loadLocalSettings()

    // Costruisce l'oggetto UserSettings
    const settings: UserSettings = {
      profile: mapBackendToProfile(userData),
      preferences: localSettings.preferences,
    }

    return settings
  },

  // Aggiorna il profilo utente (salva nel backend)
  // Ritorna i dati aggiornati dalla risposta API
  updateProfile: async (profile: Partial<UserProfile>): Promise<BackendUser> => {
    // Mappa i campi frontend -> backend
    const backendData: Record<string, string> = {}
    if (profile.firstName !== undefined) backendData.first_name = profile.firstName
    if (profile.lastName !== undefined) backendData.last_name = profile.lastName
    if (profile.email !== undefined) backendData.email = profile.email
    if (profile.language !== undefined) backendData.language = profile.language
    if (profile.workingHours !== undefined) backendData.working_hours = profile.workingHours

    // Esegui l'aggiornamento usando il nuovo endpoint update_me
    const response = await api.patch('api/accounts/users/update_me/', backendData)

    console.log('[Settings] Profile updated on backend:', backendData)

    // Ritorna i dati dalla risposta API
    return response.data as BackendUser
  },

  // Aggiorna le preferenze (salva localmente)
  updatePreferences: async (preferences: Partial<GeneralPreferences>): Promise<UserSettings> => {
    const localSettings = loadLocalSettings()
    const updatedPreferences = {
      ...localSettings.preferences,
      ...preferences,
    }
    saveLocalSettings(updatedPreferences)
    console.log('[Settings] Preferences updated locally:', updatedPreferences)

    return settingsService.getUserSettings()
  },

  // Aggiorna la foto profilo con upload multipart/form-data
  uploadProfilePhoto: async (file: File): Promise<BackendUser> => {
    const formData = new FormData()
    formData.append('profile_photo', file)

    const response = await api.patch('api/accounts/users/update_me/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })

    console.log('[Settings] Profile photo uploaded successfully')
    return response.data as BackendUser
  },

  // Aggiorna profilo con immagine (tutto insieme)
  updateProfileWithPhoto: async (
    profile: Partial<UserProfile>,
    photoFile?: File
  ): Promise<BackendUser> => {
    const formData = new FormData()

    // Aggiungi i campi del profilo
    if (profile.firstName !== undefined) formData.append('first_name', profile.firstName)
    if (profile.lastName !== undefined) formData.append('last_name', profile.lastName)
    if (profile.email !== undefined) formData.append('email', profile.email)
    if (profile.language !== undefined) formData.append('language', profile.language)
    if (profile.workingHours !== undefined) formData.append('working_hours', profile.workingHours)

    // Aggiungi la foto se presente
    if (photoFile) {
      formData.append('profile_photo', photoFile)
    }

    const response = await api.patch('api/accounts/users/update_me/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })

    console.log('[Settings] Profile with photo updated successfully')
    return response.data as BackendUser
  },
}
